/**
 * ContractFormModal — Formulaire complet BE-TAYE (crée/édite un contrat).
 * Utilise l'API interne (`POST/PATCH /contracts`). Les champs BE-TAYE sont
 * envoyés via `custom_fields.beetaye_*` OU nativement selon extension backend.
 */
import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { DatePickerFR } from "@/components/ui/date-picker-fr";
import { Loader2 } from "lucide-react";
import api, { apiError } from "@/lib/api";
import { toast } from "sonner";

const EMPTY = {
  type: "", societe: "", produit: "",
  date_signature: "", date_installation: "", duree_mois: "", date_fin: "",
  montant_abonnement: "", abonnement_gestion_interne: "", abonnement_finance: "",
  leaser: "", numero_financement: "", ca_leaser: "", rachat: "",
  vendeur: "", revendeur: "", commission: "",
  vente_materiel: "", frais_installation: "", cout_installation: "", location_serveur: "",
  banque: "", iban: "", bic: "",
  tacite_reconduction: false,
};

export default function ContractFormModal({ open, onOpenChange, contract, onSuccess, filters }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState(EMPTY);
  const isEditing = !!contract;

  useEffect(() => {
    if (contract) {
      setForm({
        type: contract.type || contract.contract_type || "",
        societe: contract.societe || contract.party_name || "",
        produit: contract.produit || "",
        date_signature: contract.date_signature || contract.start_date || "",
        date_installation: contract.date_installation || "",
        duree_mois: contract.duree_mois?.toString() || "",
        date_fin: contract.date_fin || contract.end_date || "",
        montant_abonnement: (contract.montant_abonnement ?? contract.amount_ht ?? "").toString(),
        abonnement_gestion_interne: (contract.abonnement_gestion_interne ?? "").toString(),
        abonnement_finance: (contract.abonnement_finance ?? "").toString(),
        leaser: contract.leaser || "",
        numero_financement: contract.numero_financement || "",
        ca_leaser: (contract.ca_leaser ?? "").toString(),
        rachat: (contract.rachat ?? "").toString(),
        vendeur: contract.vendeur || "",
        revendeur: contract.revendeur || "",
        commission: (contract.commission ?? "").toString(),
        vente_materiel: (contract.vente_materiel ?? "").toString(),
        frais_installation: (contract.frais_installation ?? "").toString(),
        cout_installation: (contract.cout_installation ?? "").toString(),
        location_serveur: (contract.location_serveur ?? "").toString(),
        banque: contract.banque || "",
        iban: contract.iban || "",
        bic: contract.bic || "",
        tacite_reconduction: contract.tacite_reconduction ?? contract.tacit_renewal ?? false,
      });
    } else { setForm(EMPTY); }
  }, [contract, open]);

  const upd = (k, v) => setForm((p) => ({ ...p, [k]: v }));
  const num = (v) => (v === "" || v == null ? null : parseFloat(v));
  const int = (v) => (v === "" || v == null ? null : parseInt(v, 10));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.type || !form.societe) { toast.error("Type et société obligatoires"); return; }
    setLoading(true);
    try {
      const payload = {
        type: form.type,
        societe: form.societe,
        produit: form.produit || null,
        date_signature: form.date_signature || null,
        date_installation: form.date_installation || null,
        duree_mois: int(form.duree_mois),
        date_fin: form.date_fin || null,
        montant_abonnement: num(form.montant_abonnement) ?? 0,
        abonnement_gestion_interne: num(form.abonnement_gestion_interne) ?? 0,
        abonnement_finance: num(form.abonnement_finance) ?? 0,
        leaser: form.leaser || null,
        numero_financement: form.numero_financement || null,
        ca_leaser: num(form.ca_leaser) ?? 0,
        rachat: num(form.rachat) ?? 0,
        vendeur: form.vendeur || null,
        revendeur: form.revendeur || null,
        commission: num(form.commission) ?? 0,
        vente_materiel: num(form.vente_materiel) ?? 0,
        frais_installation: num(form.frais_installation) ?? 0,
        cout_installation: num(form.cout_installation) ?? 0,
        location_serveur: num(form.location_serveur) ?? 0,
        banque: form.banque || null,
        iban: form.iban || null,
        bic: form.bic || null,
        tacite_reconduction: form.tacite_reconduction,
      };
      if (isEditing) await api.patch(`/contracts/${contract.id}`, payload);
      else await api.post("/contracts", payload);
      toast.success(isEditing ? "Contrat modifié" : "Contrat créé");
      onSuccess?.();
      onOpenChange(false);
    } catch (err) { toast.error(apiError(err)); }
    finally { setLoading(false); }
  };

  const Section = ({ title, children }) => (
    <div className="space-y-3">
      <h3 className="font-semibold text-xs text-slate-500 uppercase tracking-wider">{title}</h3>
      {children}
    </div>
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="contract-form-modal">
        <DialogHeader>
          <DialogTitle>{isEditing ? "Modifier le contrat" : "Nouveau contrat"}</DialogTitle><DialogDescription className="sr-only">Détails de la modale.</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-6 py-4">
          <Section title="Informations principales">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Type *</Label>
                <Input value={form.type} onChange={(e) => upd("type", e.target.value)}
                  list="type-options" placeholder="Ex: SAUVEGARDE, CYBERSÉCURITÉ..."
                  data-testid="input-type" />
                <datalist id="type-options">
                  {(filters?.types || []).map((t) => <option key={t} value={t} />)}
                </datalist>
              </div>
              <div className="space-y-2">
                <Label>Société / Client *</Label>
                <Input value={form.societe} onChange={(e) => upd("societe", e.target.value)}
                  list="client-options" data-testid="input-societe" />
                <datalist id="client-options">
                  {(filters?.clients || []).map((c) => <option key={c} value={c} />)}
                </datalist>
              </div>
              <div className="space-y-2">
                <Label>Produit</Label>
                <Input value={form.produit} onChange={(e) => upd("produit", e.target.value)}
                  list="produit-options" />
                <datalist id="produit-options">
                  {(filters?.produits || []).map((p) => <option key={p} value={p} />)}
                </datalist>
              </div>
              <div className="space-y-2">
                <Label>Vendeur</Label>
                <Input value={form.vendeur} onChange={(e) => upd("vendeur", e.target.value)}
                  list="vendeur-options" />
                <datalist id="vendeur-options">
                  {(filters?.vendeurs || []).map((v) => <option key={v} value={v} />)}
                </datalist>
              </div>
            </div>
          </Section>

          <Section title="Dates du contrat">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Date de signature</Label>
                <DatePickerFR value={form.date_signature} onChange={(v) => upd("date_signature", v)} />
              </div>
              <div className="space-y-2">
                <Label>Date d'installation</Label>
                <DatePickerFR value={form.date_installation} onChange={(v) => upd("date_installation", v)} />
              </div>
              <div className="space-y-2">
                <Label>Durée (mois)</Label>
                <Input type="number" value={form.duree_mois} onChange={(e) => upd("duree_mois", e.target.value)} placeholder="36" />
              </div>
              <div className="space-y-2">
                <Label>Date de fin</Label>
                <DatePickerFR value={form.date_fin} onChange={(v) => upd("date_fin", v)} />
              </div>
            </div>
          </Section>

          <Section title="Montants et abonnements (€)">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Abonnement mensuel</Label>
                <Input type="number" step="0.01" value={form.montant_abonnement} onChange={(e) => upd("montant_abonnement", e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Gestion interne</Label>
                <Input type="number" step="0.01" value={form.abonnement_gestion_interne} onChange={(e) => upd("abonnement_gestion_interne", e.target.value)} />
              </div>
              <div className="space-y-2">
                <Label>Finance / Leasing</Label>
                <Input type="number" step="0.01" value={form.abonnement_finance} onChange={(e) => upd("abonnement_finance", e.target.value)} />
              </div>
            </div>
          </Section>

          <Section title="Financement / Leasing">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2">
                <Label>Leaser</Label>
                <Input value={form.leaser} onChange={(e) => upd("leaser", e.target.value)} list="leaser-options" />
                <datalist id="leaser-options">{(filters?.leasers || []).map((l) => <option key={l} value={l} />)}</datalist>
              </div>
              <div className="space-y-2"><Label>N° Financement</Label>
                <Input value={form.numero_financement} onChange={(e) => upd("numero_financement", e.target.value)} /></div>
              <div className="space-y-2"><Label>CA Leaser (€)</Label>
                <Input type="number" step="0.01" value={form.ca_leaser} onChange={(e) => upd("ca_leaser", e.target.value)} /></div>
              <div className="space-y-2"><Label>Rachat (€)</Label>
                <Input type="number" step="0.01" value={form.rachat} onChange={(e) => upd("rachat", e.target.value)} /></div>
              <div className="space-y-2"><Label>Revendeur</Label>
                <Input value={form.revendeur} onChange={(e) => upd("revendeur", e.target.value)} list="revendeur-options" />
                <datalist id="revendeur-options">{(filters?.revendeurs || []).map((r) => <option key={r} value={r} />)}</datalist>
              </div>
              <div className="space-y-2"><Label>Commission (€)</Label>
                <Input type="number" step="0.01" value={form.commission} onChange={(e) => upd("commission", e.target.value)} /></div>
            </div>
          </Section>

          <Section title="Installation et coûts (€)">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2"><Label>Frais installation</Label>
                <Input type="number" step="0.01" value={form.frais_installation} onChange={(e) => upd("frais_installation", e.target.value)} /></div>
              <div className="space-y-2"><Label>Coût installation</Label>
                <Input type="number" step="0.01" value={form.cout_installation} onChange={(e) => upd("cout_installation", e.target.value)} /></div>
              <div className="space-y-2"><Label>Location serveur</Label>
                <Input type="number" step="0.01" value={form.location_serveur} onChange={(e) => upd("location_serveur", e.target.value)} /></div>
              <div className="space-y-2"><Label>Vente matériel</Label>
                <Input type="number" step="0.01" value={form.vente_materiel} onChange={(e) => upd("vente_materiel", e.target.value)} /></div>
            </div>
          </Section>

          <Section title="Informations bancaires">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="space-y-2"><Label>Banque</Label>
                <Input value={form.banque} onChange={(e) => upd("banque", e.target.value)} /></div>
              <div className="space-y-2 md:col-span-2"><Label>IBAN</Label>
                <Input value={form.iban} onChange={(e) => upd("iban", e.target.value)} className="font-mono text-sm" /></div>
              <div className="space-y-2"><Label>BIC</Label>
                <Input value={form.bic} onChange={(e) => upd("bic", e.target.value)} className="font-mono" /></div>
            </div>
          </Section>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>Annuler</Button>
            <Button type="submit" disabled={loading} className="bg-blue-600 hover:bg-blue-700 text-white" data-testid="submit-contract-btn">
              {loading && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {isEditing ? "Enregistrer" : "Créer le contrat"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
